import React, { useState, useEffect } from 'react';
import {
  Modal,
  Space,
  Row,
  Col,
  Typography,
  Upload,
  Button,
  Tag,
  Alert,
  Tooltip,
  App,
} from 'antd';
import {
  SafetyCertificateOutlined,
  InboxOutlined,
  FilePdfOutlined,
  EyeOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { uploadSpDocument, approveClosingRequest } from '../../../services/leadService';
import { LeadClosingRequest } from '../../../types/leads';

const { Text } = Typography;

interface ClosingApprovalModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  request: LeadClosingRequest | null;
  leadName?: string;
}

export const ClosingApprovalModal: React.FC<ClosingApprovalModalProps> = ({
  open,
  onCancel,
  onSuccess,
  request,
  leadName,
}) => {
  const { message } = App.useApp();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Clean up Object URL on unmount or file change
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!open) {
      setPdfPreviewUrl((prevUrl) => {
        if (prevUrl) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
      setSelectedFile(null);
    }
  }, [open]);

  const handleSelectFile = (file: File) => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    const newUrl = URL.createObjectURL(file);
    setPdfPreviewUrl(newUrl);
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    setSelectedFile(null);
  };

  const handleOpenPdfPreview = () => {
    if (pdfPreviewUrl) {
      window.open(pdfPreviewUrl, '_blank', 'noopener,noreferrer');
    } else {
      message.info('Belum ada berkas PDF yang dipilih.');
    }
  };

  const handleClose = () => {
    if (!submitting) {
      handleRemoveFile();
      onCancel();
    }
  };

  const handleSubmit = async () => {
    if (!request) return;

    if (!selectedFile) {
      message.error('Dokumen SP (Surat Pesanan) PDF wajib diunggah untuk menyetujui closing!');
      return;
    }

    const isPdf = selectedFile.type === 'application/pdf' || selectedFile.name?.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      message.error('Dokumen SP harus berformat PDF (.pdf)!');
      return;
    }

    const isLt5M = selectedFile.size / 1024 / 1024 <= 5;
    if (!isLt5M) {
      message.error('Ukuran berkas PDF tidak boleh melebihi 5MB!');
      return;
    }

    try {
      setSubmitting(true);
      const uploadedUrl = await uploadSpDocument(selectedFile);
      if (!uploadedUrl) {
        message.error('Gagal mengunggah berkas Surat Pesanan (SP).');
        return;
      }

      const response = await approveClosingRequest(request.id, {
        sp_document: uploadedUrl,
      });

      if (response.data?.status === 'success') {
        message.success('Pengajuan closing OK berhasil disetujui!');
        handleRemoveFile();
        onSuccess();
      } else {
        message.error(response.data?.message || 'Gagal menyetujui pengajuan.');
      }
    } catch (error: any) {
      console.error('Approve closing failed:', error);
      message.error(error.response?.data?.message || 'Gagal menyetujui pengajuan.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const displayLeadName = leadName || request?.lead_name || request?.lead?.full_name || '-';

  return (
    <Modal
      title={
        <Space size={10}>
          <SafetyCertificateOutlined style={{ color: '#7BAA18', fontSize: '20px' }} />
          <span style={{ fontSize: '16px', fontWeight: 700 }}>Persetujuan Transaksi Closing OK</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      confirmLoading={submitting}
      width={650}
      okText="Setujui & Unggah SP"
      cancelText="Batal"
      okButtonProps={{ style: { backgroundColor: '#7BAA18', borderColor: '#7BAA18', minWidth: '140px' } }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 8 }}>
        {/* Informative Transaction Overview Card */}
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '12px 16px',
          }}
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase' }}>Nama Lead</Text>
              <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>{displayLeadName}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase' }}>Pembeli</Text>
              <Text strong style={{ fontSize: '14px', color: '#0f172a' }}>{request?.buyer_name || '-'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase' }}>Unit Properti</Text>
              <Text strong style={{ fontSize: '13px', color: '#334155' }}>{request?.purchased_property_detail || '-'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', textTransform: 'uppercase' }}>Nilai Deal (Omzet)</Text>
              <Text strong style={{ fontSize: '15px', color: '#2e7d32' }}>{formatCurrency(request?.omzet || 0)}</Text>
            </Col>
          </Row>
        </div>

        {/* Upload & Mini Preview Section */}
        <div>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: '13px' }}>
              <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Dokumen Surat Pesanan (SP) Resmi
            </Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>Format wajib PDF (.pdf) • Maks. 5MB</Text>
          </div>

          {!selectedFile ? (
            <Upload.Dragger
              accept=".pdf,application/pdf"
              maxCount={1}
              fileList={[]}
              showUploadList={false}
              beforeUpload={(file) => {
                const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
                if (!isPdf) {
                  message.error('Format berkas harus PDF (.pdf)!');
                  return Upload.LIST_IGNORE;
                }
                const isLt5M = file.size / 1024 / 1024 <= 5;
                if (!isLt5M) {
                  message.error('Ukuran berkas PDF tidak boleh melebihi 5MB!');
                  return Upload.LIST_IGNORE;
                }
                handleSelectFile(file);
                return false;
              }}
              style={{
                background: '#fafafa',
                border: '1.5px dashed #cbd5e1',
                borderRadius: '10px',
                padding: '20px 16px',
              }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                <InboxOutlined style={{ color: '#7BAA18', fontSize: '38px' }} />
              </p>
              <p className="ant-upload-text" style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b', margin: '0 0 4px' }}>
                Klik atau seret berkas Surat Pesanan (SP) ke sini
              </p>
              <p className="ant-upload-hint" style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                Pastikan dokumen bertanda tangan sah dalam format PDF (maks. 5MB)
              </p>
            </Upload.Dragger>
          ) : (
            /* Selected File Mini Preview Card */
            <div
              style={{
                background: '#f0fdf4',
                border: '1.5px solid #86efac',
                borderRadius: '10px',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
              }}
            >
              <Space size={12} style={{ overflow: 'hidden' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: '#fee2e2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FilePdfOutlined style={{ fontSize: '22px', color: '#dc2626' }} />
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <Tooltip title="Klik untuk membuka dan meninjau isi berkas PDF di tab baru">
                    <Text
                      strong
                      style={{
                        fontSize: '13px',
                        color: '#1d4ed8',
                        cursor: 'pointer',
                        display: 'block',
                        textDecoration: 'underline',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '300px',
                      }}
                      onClick={handleOpenPdfPreview}
                    >
                      {selectedFile.name || 'Surat Pesanan.pdf'}
                    </Text>
                  </Tooltip>
                  <Space size={8} style={{ marginTop: 2 }}>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {formatFileSize(selectedFile.size)}
                    </Text>
                    <Tag color="success" style={{ margin: 0, fontSize: '10px', lineHeight: '18px', padding: '0 6px' }}>
                      Siap Diunggah
                    </Tag>
                  </Space>
                </div>
              </Space>

              <Space size={8}>
                <Button
                  type="primary"
                  ghost
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={handleOpenPdfPreview}
                >
                  Tinjau PDF
                </Button>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={handleRemoveFile}
                >
                  Ganti
                </Button>
              </Space>
            </div>
          )}
        </div>

        <Alert
          message="Catatan Dampak Persetujuan"
          description="Setelah disetujui, status lead akan resmi berubah menjadi O K (Closed), dicatat dalam pencapaian omzet & leaderboard, serta notifikasi persetujuan dikirim ke sales agent."
          type="info"
          showIcon
          style={{ borderRadius: '8px' }}
        />
      </Space>
    </Modal>
  );
};

export default ClosingApprovalModal;
